import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { Box, Typography, Chip, Button, TextField, Modal } from "@mui/material";
import "./reviewsAdmin.scss";

const API = "https://api.moroccool.com/api";
const STATUSES = ["held", "pending", "published", "rejected", "removed"];
const NEEDS_REASON = ["reject"];
const stars = (n) => "\u2605".repeat(n) + "\u2606".repeat(5 - n);
const LEVEL_COLOR = { LOW: "success", MEDIUM: "warning", HIGH: "error" };

const actionsFor = (status) => {
    if (status === "held" || status === "pending") return ["approve", "reject", "delete"];
    if (status === "published") return ["hold", "delete"];
    if (status === "rejected") return ["approve", "delete"];
    if (status === "removed") return ["restore", "delete"];
    return [];
};

const ReviewsAdmin = () => {
    const [status, setStatus] = useState("held");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ reviews: [], total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [venues, setVenues] = useState({});
    const [dlg, setDlg] = useState(null);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [dlgError, setDlgError] = useState("");
    const [mediaBusy, setMediaBusy] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(API + "/reviews/admin/list", {
                params: { status, page, limit: 10 },
                withCredentials: true,
            });
            setData({
                reviews: res.data.reviews || [],
                total: res.data.total || 0,
                totalPages: res.data.totalPages || 0,
            });
        } catch (err) {
            setError((err.response && err.response.data && err.response.data.message) || "Could not load reviews");
        }
        setLoading(false);
    }, [status, page]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const map = {};
        const add = (type, list) => {
            if (!Array.isArray(list)) return;
            list.forEach((v) => { map[type + ":" + v._id] = v.name || v.title || ""; });
        };
        Promise.allSettled([axios.get(API + "/cafes"), axios.get(API + "/hotels")]).then((r) => {
            if (r[0].status === "fulfilled") add("Cafe", r[0].value.data);
            if (r[1].status === "fulfilled") add("Hotel", r[1].value.data);
            setVenues(map);
        });
    }, []);

    const venueName = (rv) => venues[rv.venueModel + ":" + rv.venueId] || ("id " + rv.venueId);

    const openDialog = (review, action) => {
        setDlg({ review, action });
        setReason("");
        setDlgError("");
    };

    const submit = async () => {
        const need = NEEDS_REASON.includes(dlg.action);
        if (need && !reason.trim()) { setDlgError("A reason is required."); return; }
        setBusy(true);
        setDlgError("");
        try {
            if (dlg.action === "delete") {
                await axios.delete(API + "/reviews/admin/" + dlg.review._id, { withCredentials: true });
            } else {
                const body = { action: dlg.action };
                if (reason.trim()) body.reason = reason.trim();
                await axios.patch(API + "/reviews/admin/" + dlg.review._id + "/decision", body, { withCredentials: true });
            }
            setDlg(null);
            await load();
        } catch (err) {
            setDlgError((err.response && err.response.data && err.response.data.message) || "Action failed");
        }
        setBusy(false);
    };

    const decideMedia = async (mediaId, action) => {
        let why = "";
        if (action === "reject") {
            why = window.prompt("Reason for rejecting this photo (required):") || "";
            if (!why.trim()) return;
        }
        setMediaBusy(mediaId);
        try {
            const body = { action };
            if (action === "reject") body.reason = why.trim();
            await axios.patch(API + "/reviews/admin/media/" + mediaId, body, { withCredentials: true });
            await load();
        } catch (err) {
            setError((err.response && err.response.data && err.response.data.message) || "Photo action failed");
        }
        setMediaBusy("");
    };

    const cardStyle = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px", marginBottom: "12px" };

    return (
        <Box className="rv-dashboard">
            <Sidebar />
            <Box className="rv-main">
                <Navbar />
                <Box className="rv-content">
                    <Box className="rv-header">
                        <Box>
                            <Typography className="rv-title">Reviews</Typography>
                            <Typography className="rv-subtitle">{data.total} {status}</Typography>
                        </Box>
                        <Box>
                            <Box className="rv-tabs">
                                {STATUSES.map((s) => (
                                    <button key={s} className={"rv-tab " + (status === s ? "active" : "")}
                                        onClick={() => { setStatus(s); setPage(1); }}>{s}</button>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {error && <Typography sx={{ color: "#b91c1c", mb: 2 }}>{error}</Typography>}
                    {loading && <Typography sx={{ mb: 2 }}>Loading...</Typography>}
                    {!loading && !error && data.reviews.length === 0 && (
                        <Typography sx={{ color: "#6b7280" }}>No reviews with status "{status}".</Typography>
                    )}

                    {data.reviews.map((rv) => {
                        const risk = rv.risk || {};
                        const trust = rv.trustSnapshot || {};
                        const log = rv.moderationLog || [];
                        return (
                            <Box key={rv._id} sx={cardStyle}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {(rv.authorSnapshot && rv.authorSnapshot.username) || "?"} - {venueName(rv)} ({rv.venueModel})
                                    </Typography>
                                    <Typography sx={{ color: "#f59e0b" }}>{stars(rv.rating)}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1 }}>
                                    Sent {rv.createdAt ? new Date(rv.createdAt).toLocaleString() : "?"} | language {rv.language}
                                    {rv.editedAt ? " | edited " + new Date(rv.editedAt).toLocaleString() : ""}
                                </Typography>
                                {rv.title ? <Typography sx={{ fontWeight: 600 }}>{rv.title}</Typography> : null}
                                <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{rv.text}</Typography>

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                    <Chip size="small" color={LEVEL_COLOR[risk.level] || "default"}
                                        label={"Risk " + (risk.level || "none") + (risk.score !== undefined ? " (" + risk.score + ")" : "")} />
                                    {rv.flagged && <Chip size="small" color="warning" label="flagged" />}
                                    {rv.reportCount > 0 && <Chip size="small" color="error" label={rv.reportCount + " reports"} />}
                                    {(risk.reasons || []).map((r) => <Chip key={r} size="small" variant="outlined" label={r} />)}
                                </Box>
                                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1 }}>
                                    Account age: {trust.accountAgeDays !== undefined ? trust.accountAgeDays + " days" : "?"} |
                                    email verified: {String(trust.emailVerified)} | login: {trust.authProvider || "?"}
                                </Typography>
                                {log.length > 0 && (
                                    <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1 }}>
                                        History: {log.map((l) => l.action + (l.reason ? " (" + l.reason + ")" : "")).join(" > ")}
                                    </Typography>
                                )}

                                {Array.isArray(rv.media) && rv.media.length > 0 && (
                                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                                        {rv.media.map((m) => {
                                            const src = m.secureUrl || m.url;
                                            const st = m.verificationStatus || "pending";
                                            return (
                                                <Box key={m._id} sx={{ width: 150 }}>
                                                    <a href={src} target="_blank" rel="noreferrer">
                                                        <img src={src} alt="review photo"
                                                            style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 8, display: "block" }} />
                                                    </a>
                                                    <Chip size="small" sx={{ mt: 0.5 }}
                                                        color={st === "verified" ? "success" : st === "rejected" ? "error" : "warning"} label={st} />
                                                    {m.rejectionReason ? (
                                                        <Typography sx={{ fontSize: 11, color: "#6b7280" }}>{m.rejectionReason}</Typography>
                                                    ) : null}
                                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                                                        {st !== "verified" && (
                                                            <Button size="small" variant="outlined" disabled={mediaBusy === m._id}
                                                                onClick={() => decideMedia(m._id, "verify")}>Verify</Button>
                                                        )}
                                                        {st !== "rejected" && (
                                                            <Button size="small" variant="outlined" color="error" disabled={mediaBusy === m._id}
                                                                onClick={() => decideMedia(m._id, "reject")}>Reject</Button>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {actionsFor(rv.status).map((a) => (
                                        <Button key={a} size="small" variant="outlined"
                                            color={a === "delete" || a === "reject" ? "error" : "primary"}
                                            onClick={() => openDialog(rv, a)}>{a}</Button>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}

                    {data.totalPages > 1 && (
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
                            <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                            <Typography>Page {page} / {data.totalPages}</Typography>
                            <Button size="small" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Modal open={!!dlg} onClose={() => { if (!busy) setDlg(null); }}>
                <Box sx={{
                    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                    width: 420, maxWidth: "90vw", bgcolor: "#fff", borderRadius: "12px", p: 3
                }}>
                    {dlg && (
                        <>
                            <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                {dlg.action} this review by {(dlg.review.authorSnapshot && dlg.review.authorSnapshot.username) || "?"}?
                            </Typography>
                            {dlg.action === "delete" ? (
                                <Typography sx={{ color: "#b91c1c", mb: 2 }}>
                                    This permanently deletes the review and its ratings. It cannot be undone.
                                </Typography>
                            ) : (
                                <TextField fullWidth multiline minRows={3} size="small" sx={{ mb: 1 }}
                                    label={NEEDS_REASON.includes(dlg.action) ? "Reason (required)" : "Reason (optional)"}
                                    value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 1000 }} />
                            )}
                            {dlgError && <Typography sx={{ color: "#b91c1c", mb: 1 }}>{dlgError}</Typography>}
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button disabled={busy} onClick={() => setDlg(null)}>Cancel</Button>
                                <Button variant="contained" color={dlg.action === "delete" ? "error" : "primary"} disabled={busy} onClick={submit}>
                                    {busy ? "Working..." : dlg.action === "delete" ? "Delete forever" : "Confirm"}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </Box>
    );
};

export default ReviewsAdmin;